import React from "react";
import { api } from "@/api";
import { useTranslation } from "react-i18next";

// Multi-select combobox for actions with integrated search
// Props:
// - token: auth token
// - selected: [{id,title}]
// - onChange: (list)=>void
// - initialOptions: up to 10 active actions [{id,title}]
const ActionMultiSelect = ({ token, selected, onChange, initialOptions = [] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const containerRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced search
  React.useEffect(() => {
    let alive = true;
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.searchActions(token, { q, limit: 20 })
        .then((list) => {
          if (!alive) return;
          const opts = (list || []).map((a) => ({ id: a.id, title: a.title }));
          setResults(opts);
        })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [token, query]);

  const add = (opt) => {
    if (!opt) return;
    if (selected.some((s) => s.id === opt.id)) return;
    onChange([...selected, opt]);
    setQuery("");
  };
  const remove = (id) => onChange(selected.filter((s) => s.id !== id));
  const clearAll = () => onChange([]);

  // Compute panel list: if query empty => initialOptions; else search results
  const panelOptions = query.trim() ? results : initialOptions;
  const placeholder = query.trim() ? "Suchen..." : "Aktive Aktionen (max. 10)";

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap gap-1 mb-1">
        {selected.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1 bg-muted border rounded px-2 py-0.5 text-xs">
            {a.title}
            <button className="text-muted-foreground hover:text-foreground" onClick={() => remove(a.id)} aria-label="remove">×</button>
          </span>
        ))}
        {selected.length === 0 && (
          <span className="text-xs text-muted-foreground">{t('merchant.campaigns.filters.all')}</span>
        )}
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full border rounded px-3 py-2 text-left text-sm bg-background">
        {selected.length ? `${selected.length} ${t('merchant.campaigns.filters.active')}` : t('merchant.actions.newAction')}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-[min(320px,90vw)] bg-background border rounded shadow p-2">
          <input
            className="w-full border rounded px-2 py-1 text-sm mb-2"
            placeholder="Aktion suchen..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="max-h-48 overflow-auto">
            {!panelOptions.length && !loading && (
              <div className="p-2 text-xs text-muted-foreground">{t('merchant.campaigns.noneFound')}</div>
            )}
            {loading && (
              <div className="p-2 text-xs text-muted-foreground">Suche...</div>
            )}
            {panelOptions.map((opt) => {
              const disabled = selected.some((s) => s.id === opt.id);
              return (
                <div
                  key={opt.id}
                  className={`px-2 py-1 text-sm rounded cursor-pointer ${disabled ? 'text-muted-foreground' : 'hover:bg-muted'}`}
                  onClick={() => !disabled && add(opt)}
                >
                  {opt.title}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button className="text-xs border px-2 py-1 rounded hover:bg-muted" onClick={clearAll}>{t('merchant.campaigns.filters.all')}</button>
            <div className="text-[10px] text-muted-foreground">{placeholder}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionMultiSelect;
